import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useState,
} from "preact/compat";
import type { ForwardedRef } from "preact/compat";
import { sweCompare } from "@gss/common/sweCompare";
import { useConst } from "@gss/hooks/useConst";
import type { StringRecord } from "@gss/types/StringRecord";
import { Chart } from "../charts/Chart";

export interface Unit {
    name: string;
    orgId?: string;
    data: StringRecord<number>;
}

export interface Region {
    name: string;
    orgId: string;
    data: StringRecord<number>;
    units: Unit[];
}

interface AugmentedRegion extends Region {
    categories: string[];
    regionData: number[];
    unitsData: number[];
    visible: boolean;
}

export interface SuperGraphData {
    keys: string[];
    country: Unit;
    regions: Region[];
}

interface AugmentedSuperGraphData {
    keys: string[];
    country: Unit;
    regions: AugmentedRegion[];
}

interface Title {
    title: string;
    subtitle: string;
}

interface Callbacks {
    getTitle: (value: string) => Title;
    getCountyColor: (regionNames: string[], regionName: string) => string;
}

interface Props {
    callbacks: Callbacks;
    data: SuperGraphData;
    value: string;
    name?: string;
    suffix?: string;
    visibility?: boolean;
    axisTitle: string;
}

interface Conf {
    name: string;
    visible: boolean;
    color: string;
}

interface Serie {
    filterSerie: {
        name: string;
        color: string;
    };
    name: string;
    visible: boolean;
    color: string;
    pointStart?: number;
    fillOpacity?: number;
    lineWidth?: number;
    data: number[];
    turboThreshold: number;
    tooltip: {
        headerFormat: string | null;
        pointFormatter: () => void;
    };
    marker?: {
        enabled: false;
        states: {
            hover: {
                enabled: false;
            };
        };
    };
}

export interface SuperGraphRef {
    setVisibility: (visibility: boolean | string[]) => void;
}

export const SuperGraph = forwardRef(
    (
        { callbacks, data: dataIn, value, name, axisTitle, suffix = "" }: Props,
        fref: ForwardedRef<SuperGraphRef>,
    ) => {
        const avgSeries = useConst(getAvgSeries);
        const [title, setTitle] = useState<Title>();
        const [data, setData] = useState<AugmentedSuperGraphData>();

        useImperativeHandle(
            fref,
            () => ({
                setVisibility: (visibility: boolean | string[]) => {
                    if (data != null) {
                        const copy = { ...data };
                        for (const region of copy.regions) {
                            region.visible = Array.isArray(visibility)
                                ? visibility.includes(region.orgId)
                                : visibility;
                        }
                        setData(copy);
                    }
                },
            }),
            [data],
        );

        useEffect(() => {
            const newData: AugmentedSuperGraphData = {
                ...dataIn,
                regions: dataIn.regions.map((region) => {
                    // Sort units by value.
                    region.units.sort((a, b) => {
                        const aVal = getYValue(a, value) ?? -1;
                        const bVal = getYValue(b, value) ?? -1;
                        return bVal - aVal;
                    });

                    // Create data set for each unit in the region
                    const categories: string[] = [];
                    const unitsData: number[] = [];
                    for (const unit of region.units) {
                        const dataPoint = getYValue(unit, value);
                        // Dont use units without data.
                        if (dataPoint != null) {
                            categories.push(unit.name);
                            unitsData.push(dataPoint);
                        }
                    }

                    const regionYValue = getYValue(region, value) ?? 0;
                    // Create data set with the region overall data.
                    const regionData = Array.from(
                        { length: categories.length },
                        () => regionYValue,
                    );

                    return {
                        ...region,
                        categories,
                        regionData,
                        unitsData,
                        visible: true,
                    };
                }),
            };

            // Sort regions by name;
            newData.regions.sort((a, b) => sweCompare(a.name, b.name));

            // Set title based on new value.
            setTitle(callbacks.getTitle(name ?? value));

            setData(newData);
        }, [callbacks, dataIn, value, name]);

        if (data == null || title == null) {
            return null;
        }

        const regionNames = data.regions.map((r) => r.name);

        const _getSerie = (
            region: AugmentedRegion,
            startPoint: number,
            unitsData: number[],
        ): Serie => {
            const color = callbacks.getCountyColor(regionNames, region.name);
            return {
                filterSerie: {
                    name: region.name,
                    color,
                },
                name: region.name,
                visible: region.visible,
                color,
                pointStart: startPoint,
                data: unitsData,
                turboThreshold: 0,
                tooltip: {
                    headerFormat: null,
                    pointFormatter,
                },
            };
        };

        function pointFormatter(this: {
            series: { name: string };
            category: string;
            x: number;
        }): string {
            const regionName = this.series.name;
            const unitName = this.category;
            const unit = _getUnitByX(this.x);
            let result = `<strong>${regionName} - ${unitName}</strong><br>`;
            if (unit != null) {
                for (const key of Object.keys(unit.data)) {
                    const dataPoint = unit.data[key];
                    if (dataPoint != null) {
                        result += `${key}: ${_formatData(dataPoint)}<br>`;
                    }
                }
            }
            result += "<br><strong>Medel</strong><br>";
            return result;
        }

        const _getRegionSerie = (data: number[]) => {
            return _getAvgSerie(
                avgSeries.region,
                data,
                function pointFormatter(this: {
                    x: number;
                    y: number;
                }): string {
                    return `${_getRegionByX(this.x)?.name}: ${_formatData(this.y)}<br>`;
                },
            );
        };

        const _getCountrySerie = (size: number) => {
            const countryYValue = getYValue(data.country, value) ?? 0;
            const countryData = Array.from(
                { length: size },
                () => countryYValue,
            );
            return _getAvgSerie(
                avgSeries.country,
                countryData,
                function pointFormatter(this: { y: number }): string {
                    return `Alla: ${_formatData(this.y)}`;
                },
            );
        };

        // oxlint-disable-next-line unicorn/consistent-function-scoping
        const _getAvgSerie = (
            conf: Conf,
            data: number[],
            pointFormatter: () => void,
        ): Serie => {
            return {
                filterSerie: {
                    name: conf.name,
                    color: conf.color,
                },
                name: conf.name,
                visible: conf.visible,
                color: conf.color,
                data,
                fillOpacity: 0,
                lineWidth: 1,
                turboThreshold: 0,
                tooltip: {
                    headerFormat: null,
                    pointFormatter,
                },
                marker: {
                    enabled: false,
                    states: {
                        hover: {
                            enabled: false,
                        },
                    },
                },
            };
        };

        const _getRegionByX = (x: number) => {
            let count = 0;
            const regions = data.regions;
            for (const region of regions) {
                if (region.visible) {
                    if (count + region.units.length > x) {
                        return region;
                    }
                    count += region.units.length;
                }
            }
            return null;
        };

        const _getUnitByX = (x: number) => {
            let count = 0;
            const regions = data.regions;
            for (const region of regions) {
                if (region.visible) {
                    // This region contains the given index.
                    if (count + region.units.length > x) {
                        return region.units[x - count];
                    }
                    count += region.units.length;
                }
            }
            return null;
        };

        const _formatData = (val: number) => {
            return Math.round(val * 10) / 10 + suffix;
        };

        const _getSeriesByName = (name: string) => {
            if (name === avgSeries.country.name) {
                return avgSeries.country;
            }
            if (name === avgSeries.region.name) {
                return avgSeries.region;
            }
            return data.regions.find((r) => r.name === name);
        };

        const _showOrHide = (name: string, visible: boolean) => {
            const serie = _getSeriesByName(name);
            if (serie != null) {
                serie.visible = visible;
            }
            setData({ ...data });
        };

        const events = {
            show(this: { name: string }): void {
                _showOrHide(this.name, true);
            },
            hide(this: { name: string }): void {
                _showOrHide(this.name, false);
            },
        };

        const series: Serie[] = [];
        let categories: string[] = [];
        let regionsData: number[] = [];

        // Add series for each region.
        for (const region of data.regions) {
            const startPoint = categories.length;
            let unitsData: number[] = [];

            // Add each (visible) unit to the regions data set.
            if (region.visible) {
                unitsData = region.unitsData;
                regionsData = regionsData.concat(region.regionData);
                categories = categories.concat(region.categories);
            }
            series.push(_getSerie(region, startPoint, unitsData));
        }

        // Add regions series.
        series.push(_getRegionSerie(regionsData));

        // If not the country avg won't be shown with no regions selected.
        if (categories.length === 0) {
            categories = Array.from({ length: 100 }, () => "");
        }

        // Add country series.
        series.push(_getCountrySerie(categories.length));

        return (
            <Chart
                key={title.title + title.subtitle}
                variant="superGraph"
                filename="Super graph"
                title={title.title}
                subtitle={title.subtitle}
                yTitle={axisTitle}
                categories={categories}
                series={series}
                events={events}
            />
        );
    },
);

SuperGraph.displayName = "SuperGraph";

function getAvgSeries(): {
    region: { name: string; color: string; visible: boolean };
    country: { name: string; color: string; visible: boolean };
} {
    return {
        region: {
            name: "Medel för regionen",
            color: "#666666",
            visible: true,
        },
        country: {
            name: "Medel för alla",
            color: "#16161d",
            visible: true,
        },
    };
}

function getYValue(unit: Unit, value: string): number | undefined {
    return unit.data[value];
}
